import React, {useEffect, useRef, useState} from 'react';
import clsx from 'clsx';
import AccessoryPlacement, { PLACEMENTS } from './AccessoryPlacement';
import { setAccessories } from '../../Actions/Price.action';
import { useDispatch, useSelector } from 'react-redux';
import AccessoryDropdownItem from './AccessoryDropdownItem';

const AccessorySelector = ({ values, title }) => {
    const [ selectedAccessory, setSelectedAccessory ] = useState(null);
    const [ isOpen, setIsOpen ] = useState(false);
    const [ selectedPlacement, setSelectedPlacement ] = useState(PLACEMENTS[0]);
    const wrapperRef = useRef(null);
    
    const dispatch = useDispatch();
    const price = useSelector(state => state.price);

    const activeAccessory = selectedAccessory
        ? values.find(x => x.itemId === selectedAccessory)
        : values.find(x => x.selected);
    
    const groupId = values[0].groupId;

    function isActiveItem(value) {
        const isSelectedItemWithUrl = !value.itemId && value.url;

        if (isSelectedItemWithUrl) {
            return value.selected;
        }

        return value.itemId === activeAccessory.itemId;
    }

    function selectAccessory(value) {
        setSelectedAccessory(value.itemId);
        setIsOpen(false);
    }
    
    function renderItem(value, idx) {
        const { groupId, itemId, isDefault, url } = value;

        const dropdownMenuItemClass = clsx({
            'product-detail__dropdown-menu-item': true,
            'product-detail__dropdown-menu-item--active': (value.isEnabled && url) || !url,
        });

        return (
            <li key={idx}
                data-group-id={groupId}
                data-item-id={itemId}
                data-item-default={isDefault}
                onClick={() => selectAccessory(value)}
                className={dropdownMenuItemClass}>
                <AccessoryDropdownItem active={isActiveItem(value)}
                                       value={value}/>
            </li>
        );
    }

    useEffect(() => {
        const value = values.find(v => v.selected);

        if (value) {
            setSelectedAccessory(value.itemId);
        }
    }, [ ]);

    useEffect(() => {
        function handleClickOutside(e) {
            const $dropdown = wrapperRef.current;

            if ($dropdown == null || $dropdown.contains(e.target)) {
                return;
            }
            
            setIsOpen(false);
        }

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [ wrapperRef ]);

    useEffect(() => {
        if (selectedAccessory == null) {
            return;
        }

        const accessories = price.accessories
            .filter(a => a.itemId != null)
            .filter(a => a.groupId !== groupId);

        accessories.push({
            groupId: groupId,
            itemId: selectedAccessory
        });

        dispatch(setAccessories(accessories));
    }, [ selectedAccessory ]);

    const dropdownClassName = clsx({
        'product-detail__dropdown': true,
        'product-detail__dropdown--open': isOpen,
    });

    const hasProductImage = activeAccessory.itemId || activeAccessory.url && activeAccessory.image.url;
    const imageContainerClassName = clsx({
        'product-detail__dropdown-item-image-container': true,
        'product-detail__dropdown-item-image-container--empty': !hasProductImage,
    });

    return (
        <div ref={wrapperRef}
             className={dropdownClassName}>
            <button data-group-id={activeAccessory.groupId}
                    data-item-id={activeAccessory.itemId}
                    data-item-default={activeAccessory.isDefault}
                    data-item-placement={activeAccessory.requiresPlacement ? selectedPlacement : ''}
                    onClick={() => setIsOpen(!isOpen)}
                    className="product-detail__dropdown-button product-detail__accessory-item product-detail__accessory-item--active">
                <div>
                    <div className="product-detail__dropdown-item-container">
                        <div className={imageContainerClassName}>
                            {hasProductImage && (
                                <img src={activeAccessory.image.url}
                                     alt={activeAccessory.image.alt}
                                     className="product-detail__dropdown-item-image"/>
                            )}
                        </div>
                        <div className="product-detail__dropdown-item-body">
                            <h5 className="product-detail__dropdown-item-title">
                                {title}
                            </h5>
                            <p className="product-detail__dropdown-item-name">
                                {activeAccessory.itemName}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="product-detail__dropdown-menu-item-icon-container">
                    <img src="/ui/images/chevron-down.svg"
                         alt=""
                         className="product-detail__dropdown-menu-item-icon"/>
                </div>
            </button>
            <ul className="product-detail__dropdown-menu">
                {values.map(renderItem)}
            </ul>
            {activeAccessory.requiresPlacement && (
                <AccessoryPlacement placements={PLACEMENTS}
                                    selectedPlacement={selectedPlacement}
                                    setSelectedPlacement={setSelectedPlacement}
                                    selectedAccessory={selectedAccessory}
                                    values={values}/>
            )}
        </div>
    );
};

export default AccessorySelector;
